import {
    Component,
    Input,
    SimpleChanges,
    OnChanges,
    inject,
} from '@angular/core';
import {
    DxDataGridComponent,
    DxFormComponent,
    DxTemplateDirective,
} from 'devextreme-angular';
import {
    DxiColumnComponent,
    DxiItemComponent,
    DxoColumnChooserComponent,
    DxoExportComponent,
    DxoFilterRowComponent,
    DxoHeaderFilterComponent,
    DxoLoadPanelComponent,
    DxoPagerComponent,
    DxoPagingComponent,
    DxoPositionComponent,
    DxoScrollingComponent,
    DxoSearchComponent,
    DxoSearchPanelComponent,
    DxoSelectionComponent,
    DxoSortingComponent,
    DxoToolbarComponent,
} from 'devextreme-angular/ui/nested';
import { FilterPanelComponent } from '../../../../../components/library/filter-panel/filter-panel.component';
import {
    FilterConfig,
    GridPageComponent,
} from '../../../../common/grid-page.component';
import {
    AppointmentHistoryGridViewDto,
    AppointmentStatus,
    ShuntLocationType,
    ShuntPriority,
    StagingLane,
} from '@services';
import EnumUtils from '../../../../../shared/enum-utils';
import { CapitalCasePipe } from '../../../../../pipes/capital-case.pipe';
import { CommonModule } from '@angular/common';
import { DATE_TIME_FORMAT } from 'src/app/shared/constants';
import { UserAvatarEmailComponent } from 'src/app/components/utils/user-avatar-email/user-avatar-email.component';
import { AppointmentStatusPipe } from 'src/app/pipes/appointment-status.pipe';
import { StagingLanePipe } from 'src/app/pipes/staging-lane.pipe';
import { ShuntPriorityPipe } from 'src/app/pipes/shunt-priority.pipe';

@Component({
    selector: 'app-appointment-history',
    imports: [
        CommonModule,
        DxDataGridComponent,
        DxoScrollingComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoLoadPanelComponent,
        DxoSortingComponent,
        DxoHeaderFilterComponent,
        DxoFilterRowComponent,
        DxoColumnChooserComponent,
        DxoPositionComponent,
        DxoSearchComponent,
        DxoSearchPanelComponent,
        DxoSelectionComponent,
        DxoExportComponent,
        DxoToolbarComponent,
        DxiItemComponent,
        DxiColumnComponent,
        DxTemplateDirective,
        CapitalCasePipe,
        FilterPanelComponent,
        DxFormComponent,
        UserAvatarEmailComponent,
        StagingLanePipe,
        ShuntPriorityPipe,
    ],
    providers: [AppointmentStatusPipe, StagingLanePipe, ShuntPriorityPipe],
    templateUrl: './appointment-history.component.html',
})
export class AppointmentHistoryComponent
    extends GridPageComponent<AppointmentHistoryGridViewDto>
    implements OnChanges
{
    @Input() appointmentNumber: string | null = null;
    private appointmentStatusPipe = inject(AppointmentStatusPipe);
    private stagingLanePipe = inject(StagingLanePipe);
    private shuntPriorityPipe = inject(ShuntPriorityPipe);
    protected override exportName: string = 'Appointments Report';
    protected offset: number = 160;
    public dateTimeFormat = DATE_TIME_FORMAT;

    dataSource = this.dataSources.appointmentHistory();

    shuntLocationOptions = EnumUtils.getEnumOptions(ShuntLocationType);

    shuntPriorityOptions = EnumUtils.getEnumOptions(ShuntPriority, (v) =>
        this.shuntPriorityPipe.transform(v),
    );

    statusOptions = EnumUtils.getEnumOptions(AppointmentStatus, (v) =>
        this.appointmentStatusPipe.transform(v),
    );

    stagingLaneOptions = EnumUtils.getEnumOptions(StagingLane, (v) =>
        this.stagingLanePipe.transform(v),
    );

    assignedEmployeeFilter = this.userNameOrEmailFilter('assignedEmployee');

    protected override filterConfig: FilterConfig[] = [
        {
            field: 'employee.name',
            op: 'contains',
            customFilter: this.nameOrEmailFilter,
        },
        { field: 'created', op: '>=' },
        { field: 'status', op: '=' },
        { field: 'location', op: '=' },
        { field: 'number', op: 'contains' },
        { field: 'shuntPriority', op: '=' },
        {
            field: 'assignedEmployee.name',
            op: 'contains',
            customFilter: this.assignedEmployeeFilter,
        },
        { field: 'stagingLane', op: '=' },
        { field: 'notes', op: 'contains' },
    ];

    constructor() {
        super();
        this.init(AppointmentHistoryGridViewDto);
    }

    protected getBadgeClass(status: AppointmentStatus): string {
        return EnumUtils.getAppointmentStatusBadgeClass(status);
    }

    protected getShuntPriorityBadgeClass(status: ShuntPriority): string {
        return EnumUtils.getShuntPriorityBadgeClass(status);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['appointmentNumber']) {
            this.dataSource = this.dataSources.appointmentHistory(
                this.appointmentNumber,
            );
        }
    }
}
